import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { Person, PersonInput } from "../types";
import { Modal } from "../components/Modal";
import { initials } from "../components/util";

const EMPTY: PersonInput = {
  name: "",
  email: "",
  role: "",
  kind: "internal",
  company: "",
  notes: "",
};

export function PeoplePage() {
  const [people, setPeople] = useState<Person[]>([]);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<"all" | "internal" | "external">("all");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editing, setEditing] = useState<Person | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listPeople({ search, kind, archived: showArchived })
      .then((p) => {
        setPeople(p);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [search, kind, showArchived]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const remove = async (p: Person) => {
    if (!confirm(`Удалить «${p.name}»?`)) return;
    try {
      await api.deletePerson(p.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const archive = async (p: Person) => {
    try {
      await api.archivePerson(p.id, !p.archived);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Люди</h1>
          <p>Сотрудники и внешние участники, которых можно звать на встречи.</p>
        </div>
        <button className="primary" onClick={() => setCreating(true)}>
          + Добавить человека
        </button>
      </div>

      {error && <div className="banner red">{error}</div>}

      <div className="card">
        <div className="card-head">
          <div className="row" style={{ flex: 1 }}>
            <input
              placeholder="Поиск по имени, почте, роли, компании"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
              style={{ maxWidth: 180 }}
            >
              <option value="all">Все</option>
              <option value="internal">Сотрудники</option>
              <option value="external">Внешние</option>
            </select>
            <label
              className="row small"
              style={{ margin: 0, gap: 6, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                style={{ width: "auto" }}
              />
              показывать архив
            </label>
          </div>
          <span className="muted small">{people.length}</span>
        </div>

        {loading && people.length === 0 ? (
          <div className="empty">
            <span className="spinner" /> Загружаю…
          </div>
        ) : people.length === 0 ? (
          <div className="empty">
            <h3>Пока никого нет</h3>
            <p>Добавьте сотрудников и внешних участников — их можно будет звать на встречи.</p>
            <button className="primary" onClick={() => setCreating(true)}>
              Добавить первого
            </button>
          </div>
        ) : (
          <div className="table-wrap"><table>
            <thead>
              <tr>
                <th>Имя</th>
                <th>Почта</th>
                <th>Роль</th>
                <th>Тип</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id} style={{ opacity: p.archived ? 0.55 : 1 }}>
                  <td>
                    <div className="row" style={{ gap: 10 }}>
                      <span
                        className={`avatar ${p.kind === "external" ? "external" : ""}`}
                      >
                        {initials(p.name)}
                      </span>
                      <div>
                        <div>{p.name}</div>
                        {p.company && <div className="muted small">{p.company}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="small">{p.email}</td>
                  <td className="small">{p.role || <span className="muted">—</span>}</td>
                  <td>
                    <span className={`badge ${p.kind === "external" ? "amber" : "blue"}`}>
                      {p.kind === "external" ? "внешний" : "сотрудник"}
                    </span>
                    {!!p.archived && <span className="badge">архив</span>}
                  </td>
                  <td>
                    <div className="row nowrap" style={{ justifyContent: "flex-end" }}>
                      <button className="sm ghost" onClick={() => setEditing(p)}>
                        Править
                      </button>
                      <button className="sm ghost" onClick={() => archive(p)}>
                        {p.archived ? "Вернуть" : "В архив"}
                      </button>
                      <button className="sm ghost danger" onClick={() => remove(p)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {(creating || editing) && (
        <PersonModal
          person={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            load();
          }}
        />
      )}
    </>
  );
}

function PersonModal({
  person,
  onClose,
  onSaved,
}: {
  person: Person | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PersonInput>(
    person
      ? {
          name: person.name,
          email: person.email,
          role: person.role,
          kind: person.kind,
          company: person.company,
          notes: person.notes,
        }
      : EMPTY,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (person) await api.updatePerson(person.id, form);
      else await api.createPerson(form);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={person ? "Изменить участника" : "Новый участник"}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button className="primary" form="person-form" type="submit" disabled={saving}>
            {saving && <span className="spinner" />} Сохранить
          </button>
        </>
      }
    >
      <form id="person-form" onSubmit={save}>
        <div className="field">
          <label htmlFor="p-name">Имя</label>
          <input
            id="p-name"
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="p-email">Почта</label>
          <input
            id="p-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <div className="muted small" style={{ marginTop: 4 }}>
            На неё Zoom пришлёт приглашение.
          </div>
        </div>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="p-role">Роль</label>
            <input
              id="p-role"
              value={form.role}
              placeholder="Разработчик, дизайнер, PM…"
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="p-kind">Тип</label>
            <select
              id="p-kind"
              value={form.kind}
              onChange={(e) =>
                setForm({ ...form, kind: e.target.value as PersonInput["kind"] })
              }
            >
              <option value="internal">Сотрудник</option>
              <option value="external">Внешний участник</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="p-company">Компания</label>
          <input
            id="p-company"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="p-notes">Заметки</label>
          <textarea
            id="p-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}
